// Keep this file definitions-only. Dispatchers import these helpers while some
// registered modal components import dispatchers, so registrations live in
// modalRegistrations.ts to avoid circular initialization.
import type { RootModalDrawerOptions } from 'components/Drawers/standardDrawerContract';

import type { AsyncModalModule, ModalComponent, RegistryComponent } from './types';

export const MODAL_COMPONENTS: Record<string, RegistryComponent | undefined> = {};
const MODAL_IMPORTERS: Record<string, (() => Promise<unknown>) | undefined> = {};
const MODAL_IMPORT_PROMISES: Record<string, Promise<AsyncModalModule> | undefined> = {};
const MODAL_DRAWER_OPTIONS: Record<string, RootModalDrawerOptions | undefined> = {};
const MODAL_PRESENTATION: Record<string, string | undefined> = {};

type ModalRegistrationOptions = {
  rootModalDrawer?: RootModalDrawerOptions;
  // A registry default presentation, e.g. 'modal' to keep a type off the drawer
  // plane even when launched from a drawer. Record-level props still win.
  rootModalPresentation?: string;
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const preloadModalComponent = (key: string): Promise<AsyncModalModule> | null => {
  const getComponent = MODAL_IMPORTERS[key];
  if (!getComponent) {
    return null;
  }

  if (process.env.NODE_ENV === 'development') {
    return getComponent();
  }

  MODAL_IMPORT_PROMISES[key] ??= getComponent();
  return MODAL_IMPORT_PROMISES[key] ?? null;
};

export const isModalComponentPreloadable = (key: string): boolean => Boolean(MODAL_IMPORTERS[key]);

export const getRegisteredRootModalDrawerOptions = (key: string): RootModalDrawerOptions | undefined =>
  MODAL_DRAWER_OPTIONS[key];

export const getRegisteredRootModalPresentation = (key: string): string | undefined => MODAL_PRESENTATION[key];

const registerModalOptions = (key: string, options?: ModalRegistrationOptions): void => {
  MODAL_DRAWER_OPTIONS[key] = options?.rootModalDrawer;
  MODAL_PRESENTATION[key] = options?.rootModalPresentation;
};

// New way of registering modal components. Registering makes the modal available at the root level.
// This method accepts a lambda to dynamically import the component. The main reason to use this over
// RegisterModalComponent is to support hot reloading during development.
export const ImportModalComponent = (
  key: string,
  getComponent: () => Promise<unknown>,
  options?: ModalRegistrationOptions,
): void => {
  MODAL_IMPORTERS[key] = getComponent;
  registerModalOptions(key, options);
  Object.defineProperty(MODAL_COMPONENTS, key, {
    configurable: true,
    get: async () => {
      const component = await preloadModalComponent(key);
      return isObject(component) && component.default ? component.default : component;
    },
  });
};

// Registering a modal component adds it to the list of modals available at the root level. Consider
// using ImportModalComponent instead, in addition to adding it to list of available modals, it
// supports hot reloading in development.
export const RegisterModalComponent = (key: string, component: unknown, options?: ModalRegistrationOptions): void => {
  MODAL_COMPONENTS[key] = component as ModalComponent;
  registerModalOptions(key, options);
};
